// The trust meter (CFO plan §20, v1): what a money figure is missing, each with where to fix it.
// Empty when the data is good — the screens show nothing then. Pure.
//
// fix: 'type' | 'value' | 'estimates' | 'rate' | 'job' | 'receipts' | 'pay' (with uid) | 'metrics'

import { BILLABLE_JOB_TYPES, personRates } from './profit.js';

const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString('en-CA')}`;

/**
 * What a project's profit figures are missing. `costing` = computeProjectCosting(...) with a cfo block;
 * `names` = { [uid]: display name }. `missingMetrics` = metrics.js missingAsked(...) for the project: done tasks whose
 * asked value (paint used…) was never entered.
 */
export function projectTrust({ project, costing, job = null, names = {}, rates = null, missingMetrics = [] }) {
  const cfo = costing?.cfo;
  if (!project || !cfo) return [];
  const out = [];
  const type = cfo.type;
  if (type === 'personal') return out;                                   // not counted, nothing to trust
  if (!type) out.push({ key: 'type', text: 'No job type, so it can’t tell what this work earns', fix: 'type' });
  if (BILLABLE_JOB_TYPES.has(type) && !project.bookProjectId) out.push({ key: 'job', text: 'Not linked to a BattleBooks job, so receipts and payments don’t count', fix: 'job' });
  if (type === 'fixed') {
    if (!(cfo.revenue?.contract > 0)) out.push({ key: 'value', text: 'No contract value, so earned can’t be worked out', fix: 'value' });
    if (cfo.revenue?.pct == null) out.push({ key: 'estimates', text: 'No task estimates, so % complete is unknown', fix: 'estimates' });
  }
  if (type === 'hourly' && !(cfo.perHour?.revenue > 0) && cfo.hours > 0 && !(cfo.revenue?.booked > 0)) out.push({ key: 'rate', text: 'No bill rate on an hourly job', fix: 'rate' });
  if (Number(job?.pendingReceipts) > 0) {
    const n = Number(job.pendingReceipts);
    out.push({ key: 'receipts', text: `${n} receipt${n === 1 ? '' : 's'} (${money(job.pendingReceiptTotal)}) waiting in the books, not counted yet`, fix: 'receipts' });
  }
  for (const p of cfo.labour?.byPerson || []) {
    if (!p.rates.missing) continue;
    out.push({ key: `pay:${p.uid}`, text: `${names[p.uid] || 'Someone'} has no ${p.rates.missing}, so their hours cost $0`, fix: 'pay', uid: p.uid });
  }
  if (BILLABLE_JOB_TYPES.has(type)) out.push(...overheadTrust(rates));
  out.push(...metricTrust(missingMetrics));
  return out;
}

/** Asked values never entered (CFO 4.4): one line per metric, "3 done tasks have no Paint used". */
export function metricTrust(missing = []) {
  const by = new Map();
  for (const m of missing || []) {
    if (!by.has(m.metricId)) by.set(m.metricId, { name: m.name, tasks: new Set() });
    by.get(m.metricId).tasks.add(m.taskId);
  }
  return [...by.entries()].map(([id, g]) => ({ key: `metric:${id}`, text: `${g.tasks.size} done task${g.tasks.size === 1 ? ' has' : 's have'} no ${g.name}, so its numbers read short`, fix: 'metrics', metricId: id }));
}

/**
 * What the overhead figure is missing (phase 2), from the books' opsRates/overhead. Shown on every billable job and
 * in Setup → Money. Empty when there's nothing to say, or when this member can't see overhead (not an owner).
 */
export function overheadTrust(rates) {
  if (!rates) return [];
  const out = [];
  const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString('en-CA')}`;
  const rec = rates.recurring || {};
  if (rec.drafts > 0) out.push({ key: 'oh:drafts', text: `${rec.drafts} recurring cost${rec.drafts === 1 ? ' is a draft' : 's are drafts'} (${money(rec.draftTotal)}), not posted, so overhead reads low`, fix: 'books' });
  if (!rec.rules) out.push({ key: 'oh:recurring', text: 'No recurring costs set up (insurance, truck, phone, software, rent), so overhead reads low', fix: 'books' });
  if (Number(rates.unassignedCogs) > 0) out.push({ key: 'oh:nojob', text: `${money(rates.unassignedCogs)} of job costs aren’t tied to a job`, fix: 'books' });
  if (Number(rates.revenue) > 0 && Number(rates.pool) < Number(rates.revenue) * 0.05) out.push({ key: 'oh:thin', text: `Overhead looks thin: ${money(rates.pool)} over ${rates.window?.days || 90} days`, fix: 'money' });
  return out;
}

/** What one person's pay record is missing (the Team page). */
export function personTrust(pay) {
  const r = personRates(pay);
  const out = [];
  if (r.missing) out.push({ key: `pay:${pay?.uid || ''}`, text: `No ${r.missing}`, fix: 'pay', uid: pay?.uid || null });
  if (r.wcbCounted === false) out.push({ key: `wcb:${pay?.uid || ''}`, text: 'WCB not counted (not registered in payroll settings)', fix: 'books', info: true });
  return out;
}
